import { create } from 'zustand';
import { apiClient } from '@services/api';
import { updateMetaThemeColor } from '../utils/themeColor';

export interface DomainConfig {
  resolvedAt: 'hotel' | 'chain' | 'platform-hotel' | 'platform-chain';
  hotelId: number | null;
  hotelSlug: string | null;
  hotelName: string | null;
  hotelLogoPath: string | null;
  chainId: number;
  chainName: string;
  chainSlug: string;
  logoUrl: string | null;
  primaryColor: string | null;
  themeJson: string | null;
}

interface DomainState {
  isLoading: boolean;
  isResolved: boolean;
  config: DomainConfig | null;
  error: string | null;
  
  // Computed from URL - whether using platform domain with slugs
  isPlatformDomain: boolean;
  urlChainSlug: string | null;
  urlHotelSlug: string | null;
  
  resolveDomain: () => Promise<void>;
  getBasePath: () => string;
}

const PLATFORM_GUEST_DOMAIN = 'guest.myroomhq.com';

/**
 * Parse URL to extract slugs for platform domain routing.
 * 
 * URL patterns:
 * - Custom domain: guest.goldencrown.com/welcome -> no slugs, use domain
 * - Platform chain: guest.myroomhq.com/gc/welcome -> chainSlug=gc
 * - Platform hotel: guest.myroomhq.com/gc/herzliya/welcome -> chainSlug=gc, hotelSlug=herzliya
 */
function parseUrlSlugs(): { isPlatform: boolean; chainSlug: string | null; hotelSlug: string | null } {
  const hostname = window.location.hostname;
  const isPlatform = hostname === PLATFORM_GUEST_DOMAIN || 
                     hostname === 'localhost' || 
                     hostname.includes('azurestaticapps.net');
  
  if (!isPlatform) {
    return { isPlatform: false, chainSlug: null, hotelSlug: null };
  }
  
  // Parse path: /gc/welcome or /gc/herzliya/welcome
  const pathParts = window.location.pathname.split('/').filter(p => p);
  
  if (pathParts.length === 0) {
    return { isPlatform: true, chainSlug: null, hotelSlug: null };
  }
  
  // Known page routes (not slugs)
  const pageRoutes = ['welcome', 'order', 'activate', 'station'];
  
  // First segment is chain slug (unless it's a page route)
  const firstSegment = pathParts[0];
  if (pageRoutes.includes(firstSegment)) {
    // No slug in URL, might be a legacy or error case
    return { isPlatform: true, chainSlug: null, hotelSlug: null };
  }
  
  const chainSlug = firstSegment;
  
  // Check if second segment is a hotel slug (not a page route)
  if (pathParts.length > 1 && !pageRoutes.includes(pathParts[1])) {
    return { isPlatform: true, chainSlug, hotelSlug: pathParts[1] };
  }
  
  return { isPlatform: true, chainSlug, hotelSlug: null };
}

export const useDomainStore = create<DomainState>((set, get) => ({
  isLoading: true,
  isResolved: false,
  config: null,
  error: null,
  isPlatformDomain: false,
  urlChainSlug: null,
  urlHotelSlug: null,

  resolveDomain: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const { isPlatform, chainSlug, hotelSlug } = parseUrlSlugs();
      
      set({
        isPlatformDomain: isPlatform,
        urlChainSlug: chainSlug,
        urlHotelSlug: hotelSlug,
      });
      
      let url: string;
      
      if (isPlatform && chainSlug) {
        // Platform domain with slug
        url = `/api/v1/public/resolve-guest-domain?chainSlug=${chainSlug}`;
        if (hotelSlug) {
          url += `&hotelSlug=${hotelSlug}`;
        }
      } else if (!isPlatform) {
        // Custom domain
        url = `/api/v1/public/resolve-guest-domain?domain=${window.location.hostname}`;
      } else {
        // Platform domain without slug - show error or redirect
        set({ 
          isLoading: false, 
          isResolved: false,
          error: 'No hotel specified. Please use the full URL provided.' 
        });
        return;
      }
      
      const response = await apiClient.get(url);
      
      set({
        isLoading: false,
        isResolved: true,
        config: response.data,
      });
      
      // Apply theme from domain resolution (first load, before session validates)
      applyDomainTheme(response.data);
      
    } catch (error: unknown) {
      console.error('Failed to resolve domain:', error);
      const err = error as { response?: { data?: { error?: string | { message?: string } } } };
      const errorMessage = typeof err.response?.data?.error === 'string'
        ? err.response?.data?.error
        : (err.response?.data?.error as { message?: string })?.message || 'Failed to load configuration';
      set({ 
        isLoading: false, 
        isResolved: false,
        error: errorMessage
      });
    }
  },

  /**
   * Get the base path for routing.
   * - Custom domain: "" (routes are /welcome, /breakfast)
   * - Platform chain: "/gc" (routes are /gc/welcome)
   * - Platform hotel: "/gc/herzliya" (routes are /gc/herzliya/welcome)
   */
  getBasePath: () => {
    const { isPlatformDomain, urlChainSlug, urlHotelSlug } = get();
    
    if (!isPlatformDomain) {
      return '';
    }
    
    if (urlHotelSlug) {
      return `/${urlChainSlug}/${urlHotelSlug}`;
    }
    
    if (urlChainSlug) {
      return `/${urlChainSlug}`;
    }
    
    return '';
  },
}));

/**
 * Apply theme from domain resolution response.
 * This runs on first load, before session validation completes.
 * Stores theme in localStorage so it can be applied immediately on next visit.
 * SKIPS if session-level theme is already applied (session theme takes priority).
 */
function applyDomainTheme(config: DomainConfig) {
  const root = document.documentElement;
  
  // If session already applied a theme (e.g. from activation), don't overwrite it
  if (root.classList.contains('hotel-themed')) {
    return;
  }
  
  // Apply primary color if available
  if (config.primaryColor) {
    root.style.setProperty('--primary-color', config.primaryColor);
    root.style.setProperty('--theme-color-1', config.primaryColor);
    // Mobile Safari status-bar / Dynamic Island falls back to primary when no themeJson
    updateMetaThemeColor(config.primaryColor);
  }
  
  // Parse and apply themeJson if available
  if (config.themeJson) {
    try {
      const theme = JSON.parse(config.themeJson);
      
      // Apply theme colors
      if (theme.background) {
        root.style.setProperty('--theme-background', theme.background);
      }
      if (theme.color1) {
        root.style.setProperty('--theme-color-1', theme.color1);
      }
      if (theme.color2) {
        root.style.setProperty('--theme-color-2', theme.color2);
      }
      if (theme.color3) {
        root.style.setProperty('--theme-color-3', theme.color3);
      }
      if (theme.textColor) {
        root.style.setProperty('--theme-text-color', theme.textColor);
      }
      
      // Mark as themed
      root.classList.add('hotel-themed');

      // Sync mobile Safari status-bar / Dynamic Island colour
      updateMetaThemeColor(theme.color1 || theme.background);

      // Store in localStorage for instant load on next visit
      const themeColors = {
        background: theme.background,
        color1: theme.color1,
        color2: theme.color2,
        color3: theme.color3,
        textColor: theme.textColor,
      };
      localStorage.setItem('gcxp_theme_colors', JSON.stringify(themeColors));
    } catch (e) {
      console.warn('Failed to parse themeJson:', e);
    }
  }
}
